'use client'

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { toast } from 'sonner'
import { Search } from 'lucide-react'

import { NotificationMarkdown } from '@/components/notifications/notification-markdown'
import { Badge } from '@/components/ui/badge'
import { useCeleryWebSocketContext } from '@/contexts/celery-websocket-context'
import {
  notificationsApi,
  type UserNotification,
} from '@/lib/api/notifications'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useAuth } from '@/contexts/auth-context'

type NotificationState = UserNotification

/**
 * HTTP `/notifications/unread` при живом WebSocket всё равно нужен:
 * первый запрос при открытии вкладки до событий WS; фильтрация и счётчики считаются только на сервере,
 * а WS лишь сигнализирует об изменении (мы подтягиваем актуальный снимок одним GET).
 * Если сокет недоступен — редкий поллинг (``POLL_INTERVAL_MS``).
 */

interface NotificationsContextType {
  notifications: NotificationState[]
  unreadCount: number
  isLoading: boolean
  markAsRead: (notificationId: string) => Promise<void>
  markAsPopupShown: (notificationId: string) => Promise<void>
  refreshUnread: () => Promise<void>
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined)

/** Фолбэк при мёртвом WS: реже дергаем API, пока сокет жив — события приходят по WS. */
const POLL_INTERVAL_MS = 120000

/** Сливаем в один запрос всплеск событий WS (переподключение, несколько правок подряд). */
const WS_REFRESH_DEBOUNCE_MS = 600

function formatImportantDate(value?: string | null) {
  if (!value) return ''
  try {
    return new Date(value).toLocaleString('ru-RU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return String(value || '')
  }
}

function isAbortLikeError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const maybe = error as { code?: string; message?: string }
  if (maybe.code === 'ERR_CANCELED') return true
  const msg = (maybe.message || '').toLowerCase()
  return msg.includes('aborted') || msg.includes('canceled') || msg.includes('cancelled')
}

function isUnauthorizedLikeError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false
  const maybe = error as { response?: { status?: number } }
  return maybe.response?.status === 401
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const isGuestUser = user?.role === 'guest'
  const { onNotification, isConnected } = useCeleryWebSocketContext()
  const [notifications, setNotifications] = useState<NotificationState[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [activeImportantId, setActiveImportantId] = useState<string | null>(null)
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null)
  const [zoomImageAlt, setZoomImageAlt] = useState<string>('')
  const isRefreshingRef = useRef(false)
  const initializedRef = useRef(false)
  const wsRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const refreshUnread = useCallback(async () => {
    if (isGuestUser) {
      setNotifications([])
      setUnreadCount(0)
      setIsLoading(false)
      return
    }
    const accessToken = typeof window !== 'undefined' ? localStorage.getItem('access_token') : null
    if (!accessToken) {
      setNotifications([])
      setUnreadCount(0)
      setIsLoading(false)
      return
    }

    if (isRefreshingRef.current) return
    isRefreshingRef.current = true
    if (!initializedRef.current) {
      setIsLoading(true)
    }
    try {
      const data = await notificationsApi.getUnread({ limit: 200 })
      // Только ответ API: иначе «чужие» уведомления залипают после WS без фильтра аудитории.
      setNotifications(
        [...data.items].sort((a, b) => {
          const da = new Date(a.created_at || 0).getTime()
          const db = new Date(b.created_at || 0).getTime()
          return db - da
        }),
      )
      setUnreadCount(data.unread_count)
      initializedRef.current = true
    } catch (error) {
      if (isAbortLikeError(error)) {
        return
      }
      if (isUnauthorizedLikeError(error)) {
        // Для неавторизованного пользователя это ожидаемо:
        // не засоряем консоль, просто сбрасываем локальное состояние.
        setNotifications([])
        setUnreadCount(0)
        initializedRef.current = false
        return
      }
      console.error('Failed to refresh notifications:', error)
    } finally {
      setIsLoading(false)
      isRefreshingRef.current = false
    }
  }, [isGuestUser])

  const markAsRead = useCallback(async (notificationId: string) => {
    if (isGuestUser) {
      return
    }
    try {
      await notificationsApi.markRead(notificationId)
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId
            ? { ...item, is_read: true, read_at: new Date().toISOString() }
            : item
        )
      )
      setUnreadCount((prev) => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
      toast.error('Не удалось отметить уведомление прочитанным', { duration: Infinity })
    }
  }, [isGuestUser])

  const markAsPopupShown = useCallback(async (notificationId: string) => {
    if (isGuestUser) {
      return
    }
    try {
      await notificationsApi.markPopupShown(notificationId)
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId
            ? { ...item, was_popup_shown: true, popup_shown_at: new Date().toISOString() }
            : item
        )
      )
    } catch (error) {
      console.error('Failed to mark popup shown:', error)
    }
  }, [isGuestUser])

  /** Отложенное обновление после событий in-app по WS (не дублируем GET на каждый пакет). */
  const scheduleRefreshAfterInAppWs = useCallback(() => {
    if (wsRefreshTimerRef.current !== null) {
      clearTimeout(wsRefreshTimerRef.current)
    }
    wsRefreshTimerRef.current = setTimeout(() => {
      wsRefreshTimerRef.current = null
      void refreshUnread()
    }, WS_REFRESH_DEBOUNCE_MS)
  }, [refreshUnread])

  useEffect(() => {
    if (isGuestUser) {
      setNotifications([])
      setUnreadCount(0)
      return
    }
    void refreshUnread()
  }, [refreshUnread, isGuestUser])

  useEffect(() => {
    if (isGuestUser) {
      return
    }
    const unsubscribe = onNotification((message) => {
      if (
        message.event === 'notification_created' ||
        message.event === 'notification_updated' ||
        message.event === 'notification_deleted'
      ) {
        scheduleRefreshAfterInAppWs()
      }
    })
    return () => {
      unsubscribe()
      if (wsRefreshTimerRef.current !== null) {
        clearTimeout(wsRefreshTimerRef.current)
        wsRefreshTimerRef.current = null
      }
    }
  }, [onNotification, scheduleRefreshAfterInAppWs, isGuestUser])

  useEffect(() => {
    if (isGuestUser) {
      return
    }
    const timer = setInterval(() => {
      // Polling-фолбэк: при оффлайне сокета периодически добираем непрочитанные.
      if (!isConnected) {
        refreshUnread()
      }
    }, POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [isConnected, refreshUnread, isGuestUser])

  const importantQueue = useMemo(
    () =>
      (isGuestUser ? [] : notifications.filter(
        (item) =>
          item.is_important &&
          !item.is_deleted &&
          !item.was_popup_shown
      )),
    [notifications, isGuestUser]
  )

  const activeImportant = useMemo(() => {
    if (!importantQueue.length) return null
    if (activeImportantId) {
      return importantQueue.find((item) => item.id === activeImportantId) || importantQueue[0]
    }
    return importantQueue[0]
  }, [activeImportantId, importantQueue])

  useEffect(() => {
    if (activeImportant) {
      setActiveImportantId(activeImportant.id)
    } else {
      setActiveImportantId(null)
    }
  }, [activeImportant?.id])

  const handleCloseImportant = useCallback(async () => {
    if (!activeImportant) return
    await markAsPopupShown(activeImportant.id)
    if (!activeImportant.is_read) {
      await markAsRead(activeImportant.id)
    }
    setActiveImportantId(null)
  }, [activeImportant, markAsPopupShown, markAsRead])

  const value = useMemo<NotificationsContextType>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      markAsRead,
      markAsPopupShown,
      refreshUnread,
    }),
    [notifications, unreadCount, isLoading, markAsRead, markAsPopupShown, refreshUnread]
  )

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <Dialog open={Boolean(activeImportant)} onOpenChange={(open) => !open && handleCloseImportant()}>
        <DialogContent className="max-h-[92vh] max-w-lg gap-0 overflow-hidden border-border p-0 sm:max-w-xl">
          {activeImportant?.image_url ? (
            <button
              type="button"
              className="relative block w-full max-h-[min(38vh,300px)] min-h-[140px] overflow-hidden bg-muted text-left outline-none ring-offset-background focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => {
                setZoomImageUrl(activeImportant.image_url || null)
                setZoomImageAlt(activeImportant.title)
              }}
              aria-label="Приблизить обложку важного уведомления"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={activeImportant.image_url}
                alt=""
                className="h-full w-full max-h-[min(38vh,300px)] min-h-[140px] object-cover"
              />
              <Badge
                variant="destructive"
                className="absolute left-4 top-4 text-[11px] shadow-md backdrop-blur-[2px]"
              >
                Важное
              </Badge>
              <span className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm">
                <Search className="h-3.5 w-3.5" aria-hidden />
                Приблизить
              </span>
            </button>
          ) : null}
          <DialogHeader
            className={`space-y-1 border-b border-border bg-card px-5 text-left ${activeImportant?.image_url ? 'pt-4' : 'pt-6'}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-2 pr-8">
              <DialogTitle className="text-left text-lg font-bold leading-snug tracking-tight">
                {activeImportant?.title}
              </DialogTitle>
              {!activeImportant?.image_url ? (
                <Badge variant="destructive" className="shrink-0 text-[11px]">
                  Важное
                </Badge>
              ) : null}
            </div>
            <DialogDescription className="text-left text-xs text-muted-foreground">
              {formatImportantDate(activeImportant?.created_at) || 'Важное уведомление'}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[min(52vh,420px)]">
            <div className="px-5 py-4">
              <NotificationMarkdown content={activeImportant?.body_markdown || ''} />
            </div>
          </ScrollArea>
          <DialogFooter className="border-t border-border bg-muted/30 px-5 py-4 sm:justify-stretch">
            <Button
              type="button"
              className="w-full sm:text-base"
              size="lg"
              onClick={handleCloseImportant}
            >
              Понятно
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(zoomImageUrl)} onOpenChange={(open) => !open && setZoomImageUrl(null)}>
        <DialogContent className="!w-[98vw] !max-w-[98vw] h-[96vh] p-0 border-none bg-black/95">
          <DialogTitle className="sr-only">Увеличенное изображение уведомления</DialogTitle>
          <div className="w-full h-full flex items-center justify-center p-2">
            {zoomImageUrl ? (
              <img
                src={zoomImageUrl}
                alt={zoomImageAlt}
                className="max-w-[98vw] max-h-[94vh] object-contain"
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </NotificationsContext.Provider>
  )
}

export function useNotifications() {
  const context = useContext(NotificationsContext)
  if (!context) {
    throw new Error('useNotifications must be used within NotificationsProvider')
  }
  return context
}
